<template>
  <div v-if="product" class="configurador-item">
    <fieldset class="configurador-fields" :disabled="locked">
      <div class="configurador-resumo">
        <div>
          <strong>{{ product.descricao }}</strong>
          <small v-if="showFinancial" class="d-block text-muted">
            A partir de {{ formatMoney(product.preco) }}
          </small>
        </div>
        <span class="configurador-seguranca">
          <i class="fas fa-shield-alt" aria-hidden="true"></i>
          Valor conferido pelo sistema
        </span>
      </div>

      <section
        v-if="combos.length && !compositionItem"
        class="configurador-secao"
      >
        <h6>Combo</h6>
        <div class="configurador-opcoes">
          <label
            v-for="combo in combos"
            :key="combo.id"
            class="configurador-opcao"
            :class="{ 'is-selected': selectedComboId === combo.id }"
          >
            <input
              v-model="selectedComboId"
              type="radio"
              :name="`restaurant-combo-${_uid}`"
              :value="combo.id"
              @change="resetComboConfiguration"
            />
            <span
              ><strong>{{ combo.nome }}</strong
              ><small v-if="showFinancial">{{
                formatMoney(combo.preco_combo)
              }}</small></span
            >
          </label>
        </div>
      </section>

      <section v-if="pizza" class="configurador-secao">
        <h6>Tamanho</h6>
        <p class="text-muted">
          {{
            fixedSize
              ? "Tamanho definido no combo."
              : "Escolha o tamanho antes dos sabores."
          }}
        </p>
        <div class="configurador-opcoes">
          <label
            v-for="size in pizzaSizes"
            :key="sizeKey(size)"
            class="configurador-opcao"
            :class="{ 'is-selected': selectedSize === sizeKey(size) }"
          >
            <input
              v-model="selectedSize"
              type="radio"
              :name="`restaurant-item-size-${_uid}`"
              :value="sizeKey(size)"
              :disabled="Boolean(fixedSize)"
              @change="trimPizzaFlavors"
            />
            <span>
              <strong>{{ optionName(size) }}</strong>
              <small
                v-if="showFinancial && !selectedCombo && optionPriceCents(size)"
              >
                {{ formatMoney(optionPriceCents(size) / 100) }}
              </small>
            </span>
          </label>
        </div>

        <template v-if="selectedSizeObject">
          <h6 class="mt-5">Sabores</h6>
          <p class="text-muted">
            Escolha de 1 a {{ maxPizzaFlavors }}
            {{ maxPizzaFlavors === 1 ? "sabor" : "sabores" }}.
          </p>
          <p v-if="!compositionItem" class="configurador-regra-preco">
            O valor considera o tamanho e somente o maior acréscimo entre os
            sabores escolhidos.
          </p>
          <div class="configurador-opcoes">
            <label
              v-for="flavor in availablePizzaFlavors"
              :key="optionKey(flavor)"
              class="configurador-opcao"
              :class="{ 'is-selected': isFlavorSelected(flavor) }"
            >
              <input
                type="checkbox"
                :checked="isFlavorSelected(flavor)"
                :disabled="
                  !isFlavorSelected(flavor) &&
                  selectedFlavors.length >= maxPizzaFlavors
                "
                @change="toggleFlavor(flavor)"
              />
              <span>
                <strong>{{ optionName(flavor) }}</strong>
                <small v-if="showFinancial && flavorPriceCents(flavor)">
                  + {{ formatMoney(flavorPriceCents(flavor) / 100) }}
                </small>
              </span>
            </label>
          </div>

          <template v-if="availablePizzaBorders.length">
            <h6 class="mt-5">Borda</h6>
            <p class="text-muted">Opcional.</p>
            <div class="configurador-opcoes">
              <label
                class="configurador-opcao"
                :class="{ 'is-selected': !selectedBorder }"
              >
                <input
                  v-model="selectedBorder"
                  type="radio"
                  :name="`restaurant-item-border-${_uid}`"
                  :value="null"
                />
                <span><strong>Sem borda</strong></span>
              </label>
              <label
                v-for="border in availablePizzaBorders"
                :key="optionKey(border)"
                class="configurador-opcao"
                :class="{ 'is-selected': selectedBorder === optionKey(border) }"
              >
                <input
                  v-model="selectedBorder"
                  type="radio"
                  :name="`restaurant-item-border-${_uid}`"
                  :value="optionKey(border)"
                />
                <span>
                  <strong>{{ optionName(border) }}</strong>
                  <small v-if="showFinancial && optionPriceCents(border)">
                    + {{ formatMoney(optionPriceCents(border) / 100) }}
                  </small>
                </span>
              </label>
            </div>
          </template>
        </template>
      </section>

      <section
        v-for="group in optionGroups"
        :key="groupKey(group)"
        class="configurador-secao"
      >
        <div class="configurador-secao-titulo">
          <div>
            <h6>{{ groupName(group) }}</h6>
            <p class="text-muted">{{ groupRule(group) }}</p>
          </div>
          <span v-if="groupRequired(group)" class="badge badge-light-danger">
            Obrigatório
          </span>
        </div>

        <b-form-textarea
          v-if="isTextGroup(group)"
          :id="`restaurant-option-${_uid}-${groupKey(group)}`"
          :aria-label="groupName(group)"
          :value="selection(group)"
          rows="2"
          :maxlength="groupMaxLength(group)"
          :placeholder="group.placeholder || 'Digite a preferência do cliente'"
          @input="setTextSelection(group, $event)"
        />

        <div v-else class="configurador-opcoes">
          <div
            v-if="isSingleGroup(group) && !groupRequired(group)"
            class="configurador-opcao-wrap"
            :class="{ 'is-selected': !selection(group) }"
          >
            <label class="configurador-opcao">
              <input
                type="radio"
                :name="`restaurant-option-${_uid}-${groupKey(group)}`"
                :checked="!selection(group)"
                @change="clearOptionSelection(group)"
              />
              <span><strong>Sem opção</strong></span>
            </label>
          </div>
          <div
            v-for="option in groupOptions(group)"
            :key="optionKey(option)"
            class="configurador-opcao-wrap"
            :class="{ 'is-selected': isOptionSelected(group, option) }"
          >
            <label class="configurador-opcao">
              <input
                :type="isSingleGroup(group) ? 'radio' : 'checkbox'"
                :name="`restaurant-option-${_uid}-${groupKey(group)}`"
                :checked="isOptionSelected(group, option)"
                :disabled="isOptionDisabled(group, option)"
                @change="toggleOption(group, option)"
              />
              <span>
                <strong>{{ optionName(option) }}</strong>
                <small v-if="showFinancial && optionPriceCents(option)">
                  + {{ formatMoney(optionPriceCents(option) / 100) }}
                </small>
              </span>
            </label>
            <div
              v-if="
                groupAllowsQuantity(group) && isOptionSelected(group, option)
              "
              class="configurador-adicional-quantidade"
              :aria-label="`Quantidade de ${optionName(option)}`"
            >
              <button
                type="button"
                class="btn btn-light"
                :aria-label="`Diminuir ${optionName(option)}`"
                :disabled="optionQuantity(group, option) <= 1"
                @click="changeOptionQuantity(group, option, -1)"
              >
                <i class="fas fa-minus" aria-hidden="true"></i>
              </button>
              <strong>{{ optionQuantity(group, option) }}</strong>
              <button
                type="button"
                class="btn btn-light"
                :aria-label="`Aumentar ${optionName(option)}`"
                :disabled="!canIncreaseOptionQuantity(group, option)"
                @click="changeOptionQuantity(group, option, 1)"
              >
                <i class="fas fa-plus" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section v-if="comboItems.length" class="configurador-secao">
        <h6>Itens incluídos no combo</h6>
        <div
          v-for="item in comboItems"
          :key="`${selectedComboId}:${item.chave}`"
          class="configurador-combo-item"
        >
          <h6>
            {{ item.quantidade || 1 }} ×
            {{ item.nome || item.produto_nome || item.produto_slug }}
          </h6>
          <ConfiguradorItemRestaurante
            v-if="item.produto"
            ref="comboConfigurators"
            :product="comboItemProducts[item.chave]"
            :composition-item="item"
            :locked="locked"
            :show-financial="false"
          />
          <p v-else class="text-danger">
            Este item do combo está indisponível.
          </p>
        </div>
      </section>

      <section
        v-if="!compositionItem"
        class="configurador-secao configurador-finalizacao"
      >
        <div class="configurador-campo">
          <label for="restaurant-item-quantity">Quantidade</label>
          <div class="configurador-quantidade">
            <button
              type="button"
              class="btn btn-light"
              aria-label="Diminuir quantidade"
              :disabled="quantity <= 1"
              @click="quantity -= 1"
            >
              <i class="fas fa-minus" aria-hidden="true"></i>
            </button>
            <b-form-input
              id="restaurant-item-quantity"
              v-model.number="quantity"
              type="number"
              min="1"
              max="99"
            />
            <button
              type="button"
              class="btn btn-light"
              aria-label="Aumentar quantidade"
              :disabled="quantity >= 99"
              @click="quantity += 1"
            >
              <i class="fas fa-plus" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </section>

      <div v-if="!compositionItem" class="configurador-campo mt-4">
        <label for="restaurant-item-observation">Observação do item</label>
        <b-form-textarea
          id="restaurant-item-observation"
          v-model.trim="observation"
          rows="2"
          maxlength="500"
          placeholder="Ex.: sem cebola, servir molho à parte"
        />
      </div>

      <div
        v-if="errors.length"
        class="alert alert-light-danger mt-4 mb-0"
        role="alert"
        aria-live="assertive"
      >
        <strong>Revise a configuração:</strong>
        <ul class="mb-0 mt-2 pl-5">
          <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
        </ul>
      </div>
    </fieldset>

    <div
      v-if="quote"
      class="configurador-cotacao"
      role="status"
      aria-live="polite"
    >
      <div>
        <span>Total calculado pelo sistema</span>
        <small
          >A configuração fica bloqueada até confirmar ou recalcular.</small
        >
      </div>
      <strong>{{ formatMoney(quoteTotalCents / 100) }}</strong>
    </div>
  </div>
</template>

<script>
import {
  normalizeOperationalBoolean,
  normalizeOperationalProduct,
} from "../services/restaurantCatalog";

export default {
  name: "ConfiguradorItemRestaurante",
  props: {
    compositionItem: {
      type: Object,
      default: null,
    },
    product: {
      type: Object,
      default: null,
    },
    showFinancial: {
      type: Boolean,
      default: true,
    },
    locked: {
      type: Boolean,
      default: false,
    },
    quote: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      quantity: 1,
      observation: "",
      selectedSize: null,
      selectedFlavors: [],
      selectedBorder: null,
      selections: {},
      optionQuantities: {},
      errors: [],
      selectedComboId: null,
    };
  },
  computed: {
    combos() {
      if (this.compositionItem) return [];
      return this.product?.combos?.length
        ? this.product.combos
        : this.product?.combo
        ? [this.product.combo]
        : [];
    },
    selectedCombo() {
      return (
        this.combos.find(
          (combo) => String(combo.id) === String(this.selectedComboId)
        ) || null
      );
    },
    comboItems() {
      return this.selectedCombo?.itens || [];
    },
    comboItemProducts() {
      return this.comboItems.reduce((products, item) => {
        if (item.produto) {
          products[item.chave] = normalizeOperationalProduct({
            ...item.produto,
            combo: null,
            combos: [],
          });
        }
        return products;
      }, {});
    },
    fixedSize() {
      return (
        this.compositionItem?.pizza_tamanho_slug ||
        this.selectedCombo?.pizza_tamanho_slug ||
        null
      );
    },
    pizza() {
      return this.product?.pizza || null;
    },
    pizzaSizes() {
      const sizes = this.pizza?.tamanhos || this.pizza?.sizes || [];
      return sizes.filter(
        (size) =>
          this.asBoolean(size.ativo, true) &&
          this.asBoolean(size.active, true) &&
          this.asBoolean(size.disponivel, true) &&
          this.asBoolean(size.available, true)
      );
    },
    selectedSizeObject() {
      return (
        this.pizzaSizes.find(
          (size) => this.sizeKey(size) === this.selectedSize
        ) || null
      );
    },
    maxPizzaFlavors() {
      return Math.min(
        4,
        Math.max(
          1,
          Number(
            this.selectedSizeObject?.max_sabores ||
              this.selectedSizeObject?.max_flavors ||
              this.pizza?.max_sabores_padrao ||
              1
          ) || 1
        )
      );
    },
    availablePizzaFlavors() {
      const flavors = this.pizza?.sabores || this.pizza?.flavors || [];
      if (!this.selectedSize) {
        return [];
      }
      return flavors.filter(
        (flavor) =>
          this.asBoolean(flavor.ativo, true) &&
          this.asBoolean(flavor.active, true) &&
          this.asBoolean(flavor.disponivel, true) &&
          this.asBoolean(flavor.available, true) &&
          (!this.compositionItem?.pizza_sabores_slugs?.length ||
            this.compositionItem.pizza_sabores_slugs.includes(
              this.optionKey(flavor)
            )) &&
          this.optionSupportsSelectedSize(flavor)
      );
    },
    availablePizzaBorders() {
      const borders = this.pizza?.bordas || this.pizza?.borders || [];
      if (!this.selectedSize) {
        return [];
      }
      return borders.filter(
        (border) =>
          this.asBoolean(border.ativo, true) &&
          this.asBoolean(border.active, true) &&
          this.asBoolean(border.disponivel, true) &&
          this.asBoolean(border.available, true) &&
          this.optionSupportsSelectedSize(border)
      );
    },
    optionGroups() {
      const groups = Array.isArray(this.product?.grupos_opcoes)
        ? this.product.grupos_opcoes
        : [];
      return groups.filter(
        (group) =>
          this.asBoolean(group.ativo, true) &&
          this.asBoolean(group.active, true) &&
          this.asBoolean(group.disponivel, true) &&
          this.asBoolean(group.available, true)
      );
    },
    quoteTotalCents() {
      return Math.max(
        0,
        Number(
          this.quote?.valor_total_centavos ??
            this.quote?.total_centavos ??
            Math.round(
              Number(this.quote?.valor_total ?? this.quote?.total ?? 0) * 100
            )
        ) || 0
      );
    },
  },
  watch: {
    product: {
      immediate: true,
      handler() {
        this.reset();
      },
    },
  },
  methods: {
    asBoolean(value, fallback = false) {
      return normalizeOperationalBoolean(value, fallback);
    },
    reset() {
      this.quantity = 1;
      this.observation = "";
      this.selectedFlavors = [];
      this.selectedBorder = null;
      this.selections = {};
      this.optionQuantities = {};
      this.errors = [];
      this.selectedComboId =
        this.product?.combo?.id || this.combos[0]?.id || null;
      this.resetComboConfiguration();
    },
    resetComboConfiguration() {
      this.selectedFlavors = [];
      this.selectedBorder = null;
      this.selectedSize =
        this.fixedSize ||
        (this.pizzaSizes.length === 1
          ? this.sizeKey(this.pizzaSizes[0])
          : null);
    },
    submit() {
      this.errors = this.validate();
      if (this.errors.length) {
        return;
      }

      this.$emit("submit", this.buildPayload());
    },
    buildPayload() {
      const payload = {
        produto_id: this.product.produto_id || this.product.id,
        cardapio_produto_id: this.product.cardapio_produto_id || null,
        quantidade: Math.max(1, Math.min(99, Number(this.quantity) || 1)),
        observacao: this.observation || null,
        adicionais: this.buildAdditions(),
      };

      if (this.selectedCombo) {
        payload.combo_id = this.selectedCombo.id;
        const configurators = this.$refs.comboConfigurators || [];
        payload.combo_itens = this.comboItems.map((item) => {
          const child = configurators.find(
            (configurator) => configurator.compositionItem.chave === item.chave
          );
          const configuration = child.buildPayload();
          return {
            chave: item.chave,
            produto: item.produto_slug,
            adicionais: configuration.adicionais,
            ...(configuration.pizza ? { pizza: configuration.pizza } : {}),
          };
        });
      }

      if (this.pizza) {
        const size = this.selectedSizeObject;
        payload.pizza = {
          tamanho: size?.id ?? size?.codigo ?? this.selectedSize,
          sabores: this.selectedFlavors.slice(),
          borda: this.selectedBorder || null,
        };
      }

      return payload;
    },
    validate() {
      const errors = [];
      const configurators = this.$refs.comboConfigurators || [];
      this.comboItems.forEach((item) => {
        const child = configurators.find(
          (configurator) => configurator.compositionItem.chave === item.chave
        );
        if (!item.produto || !child) {
          errors.push(
            `${
              item.nome || item.produto_nome || item.produto_slug
            }: item do combo indisponível.`
          );
          return;
        }
        child.errors = child.validate();
        child.errors.forEach((error) =>
          errors.push(
            `${item.nome || item.produto_nome || item.produto_slug}: ${error}`
          )
        );
      });
      if (
        !Number.isFinite(Number(this.quantity)) ||
        Number(this.quantity) < 1
      ) {
        errors.push("Informe uma quantidade válida.");
      }
      if (this.pizza && !this.selectedSizeObject) {
        errors.push("Escolha o tamanho da pizza.");
      }
      if (
        this.pizza &&
        this.selectedSizeObject &&
        !this.selectedFlavors.length
      ) {
        errors.push("Escolha ao menos um sabor.");
      }
      if (
        this.pizza &&
        new Set(this.selectedFlavors).size !== this.selectedFlavors.length
      ) {
        errors.push("Não é permitido repetir o mesmo sabor.");
      }
      if (this.pizza && this.selectedFlavors.length > this.maxPizzaFlavors) {
        errors.push(`Escolha no máximo ${this.maxPizzaFlavors} sabores.`);
      }
      if (
        this.pizza &&
        this.selectedBorder &&
        !this.availablePizzaBorders.some(
          (border) => this.optionKey(border) === this.selectedBorder
        )
      ) {
        errors.push("Escolha uma borda disponível para o tamanho selecionado.");
      }

      this.optionGroups.forEach((group) => {
        const count = this.groupSelectedCount(group);
        const minimum = this.groupMinimum(group);
        const maximum = this.groupMaximum(group);
        if (count < minimum) {
          errors.push(
            this.isTextGroup(group)
              ? `${this.groupName(group)}: preencha este campo.`
              : `${this.groupName(group)}: escolha pelo menos ${minimum} opção${
                  minimum === 1 ? "" : "ões"
                }.`
          );
        }
        if (maximum && count > maximum) {
          errors.push(
            `${this.groupName(group)}: escolha no máximo ${maximum} opção${
              maximum === 1 ? "" : "ões"
            }.`
          );
        }
      });
      return errors;
    },
    buildAdditions() {
      const additions = [];
      this.optionGroups.forEach((group) => {
        const value = this.selection(group);
        if (this.isTextGroup(group)) {
          if (String(value || "").trim()) {
            additions.push({
              grupo: group.id ?? group.codigo,
              texto: String(value).trim(),
            });
          }
          return;
        }
        const values = Array.isArray(value) ? value : value ? [value] : [];
        values.forEach((selectedKey) => {
          const option = this.groupOptions(group).find(
            (item) => this.optionKey(item) === selectedKey
          );
          if (!option) {
            return;
          }
          additions.push({
            grupo: group.id ?? group.codigo,
            item: option.id ?? option.codigo,
            quantidade: this.optionQuantity(group, option),
          });
        });
      });
      return additions;
    },
    selection(group) {
      const value = this.selections[this.groupKey(group)];
      if (value !== undefined) {
        return value;
      }
      return this.isSingleGroup(group) || this.isTextGroup(group) ? "" : [];
    },
    setTextSelection(group, value) {
      this.$set(this.selections, this.groupKey(group), value);
    },
    clearOptionSelection(group) {
      const groupKey = this.groupKey(group);
      const selected = this.selection(group);
      const selectedKeys = Array.isArray(selected)
        ? selected
        : selected
        ? [selected]
        : [];

      selectedKeys.forEach((optionKey) => {
        const option = this.groupOptions(group).find(
          (item) => this.optionKey(item) === optionKey
        );
        if (option) {
          this.$delete(
            this.optionQuantities,
            this.optionQuantityKey(group, option)
          );
        }
      });

      this.$set(this.selections, groupKey, this.isSingleGroup(group) ? "" : []);
    },
    toggleOption(group, option) {
      const groupKey = this.groupKey(group);
      const optionKey = this.optionKey(option);
      if (this.isSingleGroup(group)) {
        const current = this.selection(group);
        if (current && current !== optionKey) {
          this.$delete(
            this.optionQuantities,
            this.optionQuantityKey(group, { id: current })
          );
        }
        this.$set(this.selections, groupKey, optionKey);
        this.$set(
          this.optionQuantities,
          this.optionQuantityKey(group, option),
          1
        );
        return;
      }
      const current = Array.isArray(this.selections[groupKey])
        ? this.selections[groupKey].slice()
        : [];
      const index = current.indexOf(optionKey);
      if (index >= 0) {
        current.splice(index, 1);
        this.$delete(
          this.optionQuantities,
          this.optionQuantityKey(group, option)
        );
      } else if (
        !this.groupMaximum(group) ||
        this.groupSelectedCount(group) < this.groupMaximum(group)
      ) {
        current.push(optionKey);
        this.$set(
          this.optionQuantities,
          this.optionQuantityKey(group, option),
          1
        );
      }
      this.$set(this.selections, groupKey, current);
    },
    isOptionSelected(group, option) {
      const current = this.selection(group);
      return Array.isArray(current)
        ? current.includes(this.optionKey(option))
        : current === this.optionKey(option);
    },
    isOptionDisabled(group, option) {
      if (this.isSingleGroup(group) || this.isOptionSelected(group, option)) {
        return false;
      }
      const maximum = this.groupMaximum(group);
      return Boolean(maximum && this.groupSelectedCount(group) >= maximum);
    },
    groupAllowsQuantity(group) {
      return (
        this.asBoolean(group.permite_quantidade) ||
        this.asBoolean(group.allowQuantity)
      );
    },
    optionQuantityKey(group, option) {
      return `${this.groupKey(group)}:${this.optionKey(option)}`;
    },
    optionQuantity(group, option) {
      if (!this.groupAllowsQuantity(group)) {
        return 1;
      }
      return Math.max(
        1,
        Number(
          this.optionQuantities[this.optionQuantityKey(group, option)] || 1
        )
      );
    },
    groupSelectedCount(group) {
      const value = this.selection(group);
      if (!Array.isArray(value)) {
        return String(value || "").trim() ? 1 : 0;
      }
      if (!this.groupAllowsQuantity(group)) {
        return value.length;
      }
      return value.reduce((total, selectedKey) => {
        const option = this.groupOptions(group).find(
          (item) => this.optionKey(item) === selectedKey
        );
        return total + (option ? this.optionQuantity(group, option) : 0);
      }, 0);
    },
    canIncreaseOptionQuantity(group, option) {
      const maximum = this.groupMaximum(group);
      return !maximum || this.groupSelectedCount(group) < maximum;
    },
    changeOptionQuantity(group, option, delta) {
      if (
        !this.groupAllowsQuantity(group) ||
        !this.isOptionSelected(group, option)
      ) {
        return;
      }
      const current = this.optionQuantity(group, option);
      const next = Math.max(1, Math.min(99, current + Number(delta || 0)));
      if (next > current && !this.canIncreaseOptionQuantity(group, option)) {
        return;
      }
      this.$set(
        this.optionQuantities,
        this.optionQuantityKey(group, option),
        next
      );
    },
    toggleFlavor(flavor) {
      const key = this.optionKey(flavor);
      const index = this.selectedFlavors.indexOf(key);
      if (index >= 0) {
        this.selectedFlavors.splice(index, 1);
      } else if (this.selectedFlavors.length < this.maxPizzaFlavors) {
        this.selectedFlavors.push(key);
      }
    },
    trimPizzaFlavors() {
      const available = new Set(this.availablePizzaFlavors.map(this.optionKey));
      this.selectedFlavors = this.selectedFlavors
        .filter((id) => available.has(id))
        .slice(0, this.maxPizzaFlavors);
      const borders = new Set(this.availablePizzaBorders.map(this.optionKey));
      if (this.selectedBorder && !borders.has(this.selectedBorder)) {
        this.selectedBorder = null;
      }
    },
    optionSupportsSelectedSize(option) {
      const sizes =
        option.tamanhos || option.tamanho_ids || option.available_size_ids;
      if (!Array.isArray(sizes) || !sizes.length) {
        return true;
      }
      return sizes
        .map((size) => size?.id ?? size?.codigo ?? size)
        .map(String)
        .includes(String(this.selectedSize));
    },
    isFlavorSelected(flavor) {
      return this.selectedFlavors.includes(this.optionKey(flavor));
    },
    groupOptions(group) {
      const options = group.itens || group.opcoes || group.options || [];
      return options.filter(
        (option) =>
          this.asBoolean(option.ativo, true) &&
          this.asBoolean(option.active, true) &&
          this.asBoolean(option.disponivel, true) &&
          this.asBoolean(option.available, true)
      );
    },
    groupKey(group) {
      return String(group.id ?? group.codigo ?? group.nome);
    },
    groupName(group) {
      return group.nome || group.name || "Opções";
    },
    groupType(group) {
      return group.tipo || group.type || "multipla_escolha";
    },
    isTextGroup(group) {
      return this.groupType(group) === "observacao_textual";
    },
    isSingleGroup(group) {
      return ["escolha_unica", "acompanhamento_obrigatorio"].includes(
        this.groupType(group)
      );
    },
    groupRequired(group) {
      return (
        this.asBoolean(group.obrigatorio) ||
        this.asBoolean(group.required) ||
        this.groupType(group) === "acompanhamento_obrigatorio" ||
        Number(group.minimo ?? group.min ?? 0) > 0
      );
    },
    groupMinimum(group) {
      if (group.minimo !== undefined || group.min !== undefined) {
        return Math.max(0, Number(group.minimo ?? group.min) || 0);
      }
      return this.groupRequired(group) ? 1 : 0;
    },
    groupMaximum(group) {
      if (this.isSingleGroup(group)) {
        return 1;
      }
      return Math.max(0, Number(group.maximo ?? group.max ?? 0) || 0);
    },
    groupMaxLength(group) {
      return Math.max(1, Number(group.max_caracteres || 500));
    },
    groupRule(group) {
      if (this.isTextGroup(group)) {
        return this.groupRequired(group)
          ? "Preenchimento obrigatório"
          : "Preenchimento opcional";
      }
      const minimum = this.groupMinimum(group);
      const maximum = this.groupMaximum(group);
      if (minimum && maximum) {
        return `Escolha de ${minimum} a ${maximum}`;
      }
      if (minimum) {
        return `Escolha pelo menos ${minimum}`;
      }
      if (maximum) {
        return `Escolha até ${maximum}`;
      }
      return "Opcional";
    },
    sizeKey(size) {
      return String(size.id ?? size.codigo ?? size.nome);
    },
    optionKey(option) {
      return String(option.id ?? option.codigo ?? option.nome);
    },
    optionName(option) {
      return option.nome || option.name || option.descricao || "Opção";
    },
    optionPriceCents(option) {
      const cents = Number(
        option.preco_adicional_centavos ??
          option.preco_centavos ??
          option.preco_base_centavos ??
          Math.round(Number(option.preco_base ?? option.preco ?? 0) * 100)
      );
      return Number.isFinite(cents) ? cents : 0;
    },
    flavorPriceCents(flavor) {
      const prices = flavor.precos || flavor.preco_por_tamanho || {};
      const selectedPrice = prices[this.selectedSize];
      if (selectedPrice !== undefined) {
        return Number(selectedPrice.preco_centavos ?? selectedPrice ?? 0);
      }
      return this.optionPriceCents(flavor);
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
  },
};
</script>

<style scoped>
.configurador-item {
  color: #3f4254;
}
.configurador-fields {
  border: 0;
  margin: 0;
  min-width: 0;
  padding: 0;
}
.configurador-resumo,
.configurador-secao-titulo,
.configurador-finalizacao {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}
.configurador-resumo {
  background: #f3f6f9;
  border-radius: 10px;
  padding: 12px 14px;
}
.configurador-seguranca {
  color: #1bc5bd;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: right;
}
.configurador-secao {
  border-top: 1px solid #ebedf3;
  margin-top: 18px;
  padding-top: 18px;
}
.configurador-combo-item {
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  margin-top: 14px;
  padding: 14px;
}
.configurador-secao h6,
.configurador-secao p {
  margin-bottom: 4px;
}
.configurador-regra-preco {
  background: #f3f6f9;
  border-left: 3px solid #3699ff;
  border-radius: 4px;
  color: #5e6278;
  margin-top: 8px;
  padding: 8px 10px;
}
.configurador-opcoes {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  margin-top: 10px;
}
.configurador-opcao {
  align-items: center;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  gap: 10px;
  margin: 0;
  min-height: 48px;
  padding: 9px 11px;
}
.configurador-opcao-wrap {
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  overflow: hidden;
}
.configurador-opcao-wrap .configurador-opcao {
  border: 0;
  border-radius: 0;
}
.configurador-opcao-wrap.is-selected {
  background: #f1f8ff;
  border-color: #3699ff;
}
.configurador-opcao.is-selected {
  background: #f1f8ff;
  border-color: #3699ff;
}
.configurador-opcao input {
  flex: 0 0 auto;
}
.configurador-opcao span {
  display: flex;
  flex: 1;
  justify-content: space-between;
  gap: 8px;
}
.configurador-opcao small {
  color: #3699ff;
  white-space: nowrap;
}
.configurador-adicional-quantidade {
  align-items: center;
  border-top: 1px solid #e4e6ef;
  display: grid;
  grid-template-columns: 44px minmax(36px, 1fr) 44px;
  text-align: center;
}
.configurador-adicional-quantidade .btn {
  border-radius: 0;
  min-height: 44px;
}
.configurador-cotacao {
  align-items: center;
  background: #e8fff3;
  border: 1px solid #b6f4d0;
  border-radius: 10px;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-top: 18px;
  padding: 14px 16px;
}
.configurador-cotacao span,
.configurador-cotacao small {
  display: block;
}
.configurador-cotacao span {
  color: #181c32;
  font-weight: 700;
}
.configurador-cotacao small {
  color: #5e6278;
}
.configurador-cotacao strong {
  color: #0bb783;
  font-size: 1.25rem;
  white-space: nowrap;
}
.configurador-campo {
  flex: 1;
}
.configurador-campo > label {
  font-weight: 600;
}
.configurador-quantidade {
  display: grid;
  grid-template-columns: 44px minmax(70px, 100px) 44px;
}
.configurador-quantidade .btn,
.configurador-quantidade .form-control {
  border-radius: 0;
  min-height: 44px;
  text-align: center;
}
.configurador-quantidade .btn:first-child {
  border-radius: 6px 0 0 6px;
}
.configurador-quantidade .btn:last-child {
  border-radius: 0 6px 6px 0;
}
@media (max-width: 575.98px) {
  .configurador-resumo,
  .configurador-finalizacao {
    align-items: stretch;
    flex-direction: column;
  }
  .configurador-seguranca {
    text-align: left;
  }
  .configurador-opcoes {
    grid-template-columns: 1fr;
  }
  .configurador-cotacao {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
